import {Actions} from '@redstack/shared'
import {createFileRoute} from '@tanstack/react-router'
import {PageHeader} from '@/components/editorial'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {MonitoringDashboard} from '@/features/monitoring/MonitoringDashboard'
import {MonitoringPageSkeleton} from '@/features/monitoring/MonitoringPageSkeleton'
import {PermitIntakeTab} from '@/features/monitoring/PermitIntakeTab'
import {
	useWorkflowsDashboard,
	workflowsDashboardQueryOptions,
} from '@/hooks/monitoring'
import {useRequirePermission} from '@/hooks/useRequirePermission'

function MonitoringPageGuard() {
	if (!useRequirePermission(Actions.MONITORING_VIEW)) return null
	return <MonitoringPage />
}

function MonitoringPage() {
	const {data, isLoading, isError, isRefetching, refetch} =
		useWorkflowsDashboard()

	if (isLoading) {
		return <MonitoringPageSkeleton />
	}

	if (isError) {
		return (
			<div className='flex items-center justify-center min-h-[400px]'>
				<div className='text-destructive'>Failed to load workflow data</div>
			</div>
		)
	}

	if (!data) {
		return null
	}

	return (
		<div className='container mx-auto px-8 pt-4 pb-8 max-w-7xl h-full overflow-auto'>
			<PageHeader
				size='compact'
				title='Monitoring'
				subtitle='Scheduled permit fetches and their health.'
			/>

			<Tabs defaultValue='workflows'>
				<div className='mb-4'>
					<TabsList>
						<TabsTrigger value='workflows'>Workflows</TabsTrigger>
						<TabsTrigger value='permit-intake'>Permit Intake</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent value='workflows'>
					<MonitoringDashboard
						data={data}
						isRefetching={isRefetching}
						onRefresh={refetch}
					/>
				</TabsContent>

				<TabsContent value='permit-intake'>
					<PermitIntakeTab />
				</TabsContent>
			</Tabs>
		</div>
	)
}

export const Route = createFileRoute('/monitoring/')({
	component: MonitoringPageGuard,
	loader: ({context}) =>
		context.queryClient.ensureQueryData(workflowsDashboardQueryOptions()),
})
